import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaSchool, FaGraduationCap } from "react-icons/fa";
import ProtectedRoute from "../../components/ProtectedRoute";

const API_BASE = "http://esnakademi.com/api";

export const ClassBranchAdminPage = () => {
  const [classes, setClasses] = useState([]);
  const [newClass, setNewClass] = useState("");
  const [branchInputs, setBranchInputs] = useState({}); // {classId: branchName}
  const [editingClass, setEditingClass] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(false);

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    "Content-Type": "application/json"
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log("Sınıflar getiriliyor...");
      const cls = await axios.get(`${API_BASE}/classes`, { headers });
      console.log("Sınıflar response:", cls.data);
      
      const withBranches = await Promise.all(
        cls.data.map(async (c) => {
          console.log(`Sınıf ${c.id} için şubeler getiriliyor...`);
          const timestamp = new Date().getTime();
          const br = await axios.get(`${API_BASE}/branches?class_id=${c.id}&_t=${timestamp}`, { headers });
          console.log(`Sınıf ${c.id} şubeleri response:`, br.data);
          return { ...c, branches: br.data || [] };
        })
      );
      console.log("Tüm veriler:", withBranches);
      setClasses(withBranches);
    } catch (e) {
      console.error("Veri yükleme hatası:", e);
      toast.error(`Veriler yüklenemedi: ${e.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const addClass = async () => {
    if (!newClass.trim()) {
      toast.error("Sınıf seviyesi boş olamaz");
      return;
    }
    try {
      await axios.post(`${API_BASE}/classes`, { class_level: newClass.trim() }, { headers });
      toast.success("Sınıf başarıyla eklendi");
      setNewClass("");
      fetchData();
    } catch (e) {
      toast.error("Sınıf eklenemedi");
    }
  };

  const addBranch = async (classId) => {
    const name = branchInputs[classId];
    if (!name?.trim()) {
      toast.error("Şube adı boş olamaz");
      return;
    }
    try {
      const response = await axios.post(
        `${API_BASE}/branches`,
        { class_id: classId, branch_name: name.trim().normalize('NFC').toLocaleUpperCase('tr-TR') },
        { headers }
      );
      console.log("Şube ekleme response:", response.data);
      toast.success("Şube başarıyla eklendi");
      setBranchInputs((p) => ({ ...p, [classId]: "" }));
      console.log("Şube eklendi, veriler yenileniyor...");
      
      // Alternatif: Sadece o sınıfın şubelerini yeniden yükle
      const timestamp = new Date().getTime();
      const branchesResponse = await axios.get(`${API_BASE}/branches?class_id=${classId}&_t=${timestamp}`, { headers });
      console.log("Yeni şube listesi:", branchesResponse.data);
      
      // State'i güncelle
      setClasses(prevClasses => 
        prevClasses.map(cls => 
          cls.id === classId 
            ? { ...cls, branches: branchesResponse.data || [] }
            : cls
        )
      );
      
    } catch (e) {
      console.error("Şube ekleme hatası:", e);
      if (e.response?.status === 400) {
        toast.error("Gerekli alanlar eksik");
      } else if (e.response?.status === 409) {
        toast.error("Bu şube adı zaten mevcut");
      } else if (e.response?.status === 401) {
        toast.error("Yetkiniz yok");
      } else {
        toast.error(`Şube eklenemedi: ${e.response?.data?.message || e.message}`);
      }
    }
  };

  const deleteClass = async (classId, className) => {
    if (!window.confirm(`"${className}. Sınıf" ve tüm şubelerini silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/classes/${classId}`, { headers });
      toast.success("Sınıf başarıyla silindi");
      fetchData();
    } catch (e) {
      toast.error("Sınıf silinemedi");
    }
  };

  const deleteBranch = async (branchId, branchName, classId) => {
    if (!window.confirm(`"${branchName}" şubesini silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/branches/${branchId}`, { headers });
      toast.success("Şube başarıyla silindi");
      
      // Sadece o sınıfın şubelerini yeniden yükle
      const timestamp = new Date().getTime();
      const branchesResponse = await axios.get(`${API_BASE}/branches?class_id=${classId}&_t=${timestamp}`, { headers });
      
      // State'i güncelle
      setClasses(prevClasses => 
        prevClasses.map(cls => 
          cls.id === classId 
            ? { ...cls, branches: branchesResponse.data || [] }
            : cls
        )
      );
    } catch (e) {
      console.error("Şube silme hatası:", e);
      toast.error(`Şube silinemedi: ${e.response?.data?.message || e.message}`);
    }
  };

  const startEditClass = (classItem) => {
    setEditingClass(classItem.id);
    setEditValues({ ...editValues, [`class_${classItem.id}`]: classItem.class_level });
  };

  const startEditBranch = (branch) => {
    setEditingBranch(branch.id);
    setEditValues({ ...editValues, [`branch_${branch.id}`]: branch.branch_name });
  };

  const cancelEdit = () => {
    setEditingClass(null);
    setEditingBranch(null);
    setEditValues({});
  };

  const saveEditClass = async (classId) => {
    const newValue = editValues[`class_${classId}`];
    if (!newValue?.trim()) {
      toast.error("Sınıf seviyesi boş olamaz");
      return;
    }
    try {
      await axios.put(`${API_BASE}/classes/${classId}`, { class_level: newValue.trim() }, { headers });
      toast.success("Sınıf başarıyla güncellendi");
      setEditingClass(null);
      setEditValues({});
      fetchData();
    } catch (e) {
      toast.error("Sınıf güncellenemedi");
    }
  };

  const saveEditBranch = async (branchId, classId) => {
    const newValue = editValues[`branch_${branchId}`];
    if (!newValue?.trim()) {
      toast.error("Şube adı boş olamaz");
      return;
    }
    try {
      await axios.put(`${API_BASE}/branches/${branchId}`, { branch_name: newValue.trim().normalize('NFC').toLocaleUpperCase('tr-TR') }, { headers });
      toast.success("Şube başarıyla güncellendi");
      setEditingBranch(null);
      setEditValues({});
      
      // Sadece o sınıfın şubelerini yeniden yükle
      const timestamp = new Date().getTime();
      const branchesResponse = await axios.get(`${API_BASE}/branches?class_id=${classId}&_t=${timestamp}`, { headers });
      
      // State'i güncelle
      setClasses(prevClasses => 
        prevClasses.map(cls => 
          cls.id === classId 
            ? { ...cls, branches: branchesResponse.data || [] }
            : cls
        )
      );
    } catch (e) {
      console.error("Şube güncelleme hatası:", e);
      toast.error(`Şube güncellenemedi: ${e.response?.data?.message || e.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <span className="ml-3 text-gray-600">Veriler yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Yeni Sınıf Ekleme */}
      <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-xl border border-green-200">
        <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
          <FaSchool className="mr-2" />
          Yeni Sınıf Ekle
        </h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={newClass}
            onChange={(e) => setNewClass(e.target.value)}
            placeholder="Sınıf seviyesi (örn: 4, 5, 6...)"
            className="flex-1 px-4 py-3 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            onKeyPress={(e) => e.key === 'Enter' && addClass()}
          />
          <button
            onClick={addClass}
            className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all duration-300 flex items-center font-medium"
          >
            <FaPlus className="mr-2" />
            Sınıf Ekle
          </button>
        </div>
      </div>

      {/* Sınıf Listesi */}
      <div className="space-y-4">
        {classes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FaGraduationCap className="mx-auto text-4xl mb-4 text-gray-400" />
            <p>Henüz sınıf eklenmemiş</p>
          </div>
        ) : (
          classes.map((classItem) => (
            <div key={classItem.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              {/* Sınıf Başlığı */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaSchool className="text-green-600 mr-3" />
                    {editingClass === classItem.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editValues[`class_${classItem.id}`] || ''}
                          onChange={(e) => setEditValues({ ...editValues, [`class_${classItem.id}`]: e.target.value })}
                          className="px-3 py-1 border border-gray-300 rounded text-lg font-semibold"
                          autoFocus
                        />
                        <span className="text-lg font-semibold text-gray-700">. Sınıf</span>
                      </div>
                    ) : (
                      <h3 className="text-lg font-semibold text-gray-800">
                        {classItem.class_level}. Sınıf
                      </h3>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {editingClass === classItem.id ? (
                      <>
                        <button
                          onClick={() => saveEditClass(classItem.id)}
                          className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-50 transition-colors"
                          title="Kaydet"
                        >
                          <FaCheck />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="İptal"
                        >
                          <FaTimes />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditClass(classItem)}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Düzenle"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => deleteClass(classItem.id, classItem.class_level)}
                          className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="Sil"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Şube Ekleme */}
              <div className="p-6 bg-blue-50 border-b border-gray-200">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={branchInputs[classItem.id] || ""}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Türkçe karakter desteği: i -> İ, ı -> I
                      value = value.replace(/i/g, 'İ').replace(/ı/g, 'I');
                      setBranchInputs({ ...branchInputs, [classItem.id]: value });
                    }}
                    placeholder="Yeni şube adı (örn: A, B, C...)"
                    className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                    onKeyPress={(e) => e.key === 'Enter' && addBranch(classItem.id)}
                  />
                  <button
                    onClick={() => addBranch(classItem.id)}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-all duration-300 flex items-center font-medium"
                  >
                    <FaPlus className="mr-2" />
                    Şube Ekle
                  </button>
                </div>
              </div>

              {/* Şube Listesi */}
              <div className="p-6">
                {classItem.branches.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Bu sınıfta henüz şube bulunmuyor</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {classItem.branches.map((branch) => (
                      <div key={branch.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                        {editingBranch === branch.id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={editValues[`branch_${branch.id}`] || ''}
                              onChange={(e) => {
                                let value = e.target.value;
                                // Türkçe karakter desteği: i -> İ, ı -> I
                                value = value.replace(/i/g, 'İ').replace(/ı/g, 'I');
                                setEditValues({ ...editValues, [`branch_${branch.id}`]: value });
                              }}
                              className="px-2 py-1 border border-gray-300 rounded text-sm font-medium flex-1 uppercase"
                              autoFocus
                            />
                            <div className="flex gap-1">
                              <button
                                onClick={() => saveEditBranch(branch.id, classItem.id)}
                                className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors"
                                title="Kaydet"
                              >
                                <FaCheck size={12} />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                                title="İptal"
                              >
                                <FaTimes size={12} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="font-medium text-gray-700">{branch.branch_name} Şubesi</span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => startEditBranch(branch)}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                                title="Düzenle"
                              >
                                <FaEdit size={12} />
                              </button>
                              <button
                                onClick={() => deleteBranch(branch.id, branch.branch_name, classItem.id)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Sil"
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default () => (
  <ProtectedRoute allowedRoles={["admin"]}>
    <ClassBranchAdminPage />
  </ProtectedRoute>
); 